<template>
  <div
    :class="[
      'plan',
      isPlanSelected && 'plan__selected',
      isCurrentPlan && 'plan__current-plan',
      isPremium && 'plan__premium',
    ]"
    @click="handleUpdatePlan(plan)"
  >
    <div
      class="plan__header-info"
      :class="isPremium && 'plan__header-info--best-value'"
    >
      <div v-if="isMonthly" class="plan__header-info__content">
        Introductory Content
      </div>
      <div v-if="isAnnual" class="plan__header-info__content">
        Introductory Content
        <span class="plan__header-info__content--purple"
          >Free eJPT and ICCA voucher</span
        >
      </div>
      <div v-if="isPremium" class="plan__header-info__content">
        Entire Content Library <strong>BEST VALUE</strong>
      </div>
    </div>
    <div
      :class="[
        'plan__container',
        (isMonthlyPlanDisabled || isCurrentPlan) &&
          'plan__container--unselectable',
        isPremium && 'plan__container__premium',
      ]"
    >
      <div
        v-if="isCurrentPlan"
        class="pill pill--secondary plan__current-plan-pill"
      >
        Your plan
      </div>
      <div
        v-else
        :class="[
          'plan__radio-button',
          isPlanSelected && 'plan__radio-button--selected',
        ]"
      ></div>
      <div class="plan__name-wrapper">
        <p class="plan__name">{{ cleanName(plan.name) }}</p>
      </div>
      <div v-if="!isCurrentPlan" class="plan__price-container">
        <p class="plan__price">{{ cleanPrice(plan.price) }}</p>
        <p class="plan__term">/{{ planTerm(plan) }}</p>
      </div>
      <div class="plan__details-container">
        <p
          v-for="detail in plan.details"
          :key="`detail-${detail.name}`"
          class="plan__detail"
        >
          <CheckIcon
            v-if="detail.checked"
            class="plan__flair plan__flair--checked"
          />
          <XIcon v-else class="plan__flair plan__flair--unchecked" />
          <span
            :class="[
              'plan__flair-info',
              !detail.checked && 'plan__flair-info--unchecked',
            ]"
            >{{ detail.name }}</span
          >
        </p>
        <div class="plan__details-learn-more-wrapper">
          <a
            :href="moreInfoUrl"
            class="plan__details-learn-more-link"
            target="_blank"
          >
            See What's Included
          </a>
          <span
            v-if="isPremium"
            class="plan__details-learn-more-sd-not-included"
          >
            *Skill Dive Not Included
          </span>
        </div>
      </div>
    </div>
    <div v-if="isPremium" class="plan__fake-footer-premium"></div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import CONSTANTS from '@/constants'
import { CheckIcon, XIcon } from 'vue-feather-icons'

export default {
  name: 'Plan',
  components: {
    CheckIcon,
    XIcon,
  },
  props: {
    plan: {
      type: Object,
      required: true,
    },
    hasMonthlyPlan: {
      type: Boolean,
      required: true,
    },
    selectedPlan: {
      type: Object,
      required: false,
    },
    moreInfoUrl: {
      type: String,
      required: false,
    },
  },
  computed: {
    ...mapState({
      premiumPlanCode: state => state.subscriptions.premiumPlanCode,
    }),
    ...mapGetters({
      usersSubscriptions: 'subscriptions/usersSubscriptions',
      activePlans: 'subscriptions/activePlans',
    }),
    isPremiumPlusPlan() {
      return (
        this.plan?.merchant_id ===
        CONSTANTS.PLANS.PREMIUM_PLANS.INE_PREMIUM_DEFENSE
      )
    },
    isMonthly() {
      return this.plan?.merchant_id === CONSTANTS.PLANS.ALL.fundamentals_monthly
    },
    isAnnual() {
      return this.plan?.merchant_id === CONSTANTS.PLANS.ALL.fundamentals_annual
    },
    isPremium() {
      return this.plan?.merchant_id === CONSTANTS.PLANS.ALL.ine_premium
    },
    isCurrentMonthlyPlan() {
      return (
        this.plan?.merchant_id === CONSTANTS.PLANS.MONTHLY &&
        this.hasMonthlyPlan
      )
    },
    isMonthlyPlanDisabled() {
      const {
        hasMonthlyPlan,
        plan: { merchant_id },
      } = this
      return (
        !hasMonthlyPlan &&
        merchant_id === CONSTANTS.PLANS.MONTHLY &&
        merchant_id !== this.currentPlan
      )
    },
    isPlanSelected() {
      return this.selectedPlan?.name === this.plan.name
    },
    isCurrentPlan() {
      const {
        plan: { merchant_id },
      } = this
      return this.currentPlan === merchant_id
    },
    currentPlan() {
      const filteredPlans = this.activePlans.filter(
        plan => plan.plan_code !== CONSTANTS.PLANS.ALL.skill_dive_annual
      )
      return filteredPlans && filteredPlans.length
        ? filteredPlans[0]?.plan_code
        : null
    },
  },
  methods: {
    cleanName(name) {
      if (name === 'Premium Defense Bundle') return 'Premium+'
      if (name === 'Premium') return 'Premium Annual'
      return name.replace('Individual', '')
    },
    cleanPrice(price) {
      const priceParts = price.split('.')
      return priceParts[1] === '00' ? priceParts[0] : price
    },
    planTerm(plan) {
      return [
        CONSTANTS.PLANS.ALL.premium_monthly,
        CONSTANTS.PLANS.ALL.fundamentals_monthly,
      ].includes(plan.merchant_id)
        ? 'month'
        : plan.term
    },
    handleUpdatePlan(plan) {
      if (this.isCurrentPlan || this.isMonthlyPlanDisabled) return
      this.$emit('update-selected-plan', plan)
    },
  },
}
</script>

<style lang="scss">
.plan {
  display: flex;
  flex-direction: column;
  align-self: stretch;
  width: 100%;
  margin-bottom: 10px;
  background: var(--glass-mid-gray);
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  &__premium {
    border-left: 10px solid #863ad2;
    border-right: 10px solid #5936e4;
    box-sizing: border-box;
  }
  @include mobile {
    width: calc(330px + 5px);
  }
  &__container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    color: var(--white);
    background: var(
      --glass-mid-gray,
      linear-gradient(
        105deg,
        rgba(62, 63, 73, 0.7) -13.91%,
        rgba(62, 63, 73, 0) 100.48%
      )
    );
    position: relative;
    transition: 0.5s;
    padding: 30px 20px 20px;
    width: 100%;
    max-width: 100%;
    height: 100%;
    flex: 1 0 25%;

    &:hover:not(.plan__container--unselectable) {
      background: var(--glass-extra-light);
    }
    &:active:not(.plan__container--unselectable) {
      background: var(--glass-dark);
    }
    &__premium {
      background: var(--glass-mid-gray);
    }
  }
  &__header-info {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px;
    background: var(--glass-extra-light);
    &__content {
      font-size: rem(12);
      line-height: 14px;
      color: var(--white);
      text-align: center;
      &--purple {
        color: var(--purple-pale);
      }
    }
    &--best-value {
      background: var(--purple-gradient);
    }
  }
  &__fake-footer-premium {
    position: absolute;
    bottom: 0;
    left: 0;
    height: 10px;
    width: 100%;
    background: var(--purple-gradient);
  }
  &__current-plan-pill {
    width: auto;

    position: absolute;
    right: 12px;
    top: 15px;
  }
  &__radio-button {
    position: absolute;
    top: 20px;
    right: 30px;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: solid 1px var(--athens-gray);
    &--selected {
      background: rgba(0, 218, 153, 0.1);
      border: 1px solid var(--positive-light);

      &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: var(--athens-gray);
      }
    }
  }
  &__current-plan {
    cursor: default;
  }
  &__name {
    font-size: rem(24px);
    line-height: 32px;
    margin: 0;
    width: 100%;
    text-transform: capitalize;
    font-family: var(--font-bold);
    text-align: left;
    margin-top: 20px;
  }
  &__price-container {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-bottom: 30px;
  }
  &__details {
    &-container {
      padding-top: 30px;
      border-top: 1px solid var(--tundora-gray);

      display: flex;
      flex-direction: column;
    }
    &-learn-more {
      &-wrapper {
        margin-top: 30px;
      }

      &-link {
        color: var(--purple-pale);
        font-feature-settings: 'clig' off, 'liga' off;

        font-family: var(--font-book);
        font-size: 0.875rem;
        font-style: normal;
        font-weight: 450;
        line-height: 1.25rem;
      }

      &-sd-not-included {
        color: var(--light-blue-gray);
        font-size: 0.75rem;
        font-style: italic;
        font-weight: 450;
        line-height: 0.875rem;
      }
    }
  }
  &__price {
    margin: 0;
    font-family: var(--font-bold);
    font-size: rem(54);
    line-height: 66px;
  }
  &__term {
    color: var(--athens-gray);
    font-size: rem(12px);
    margin: 0 0 0 5px;
  }
  &__detail {
    padding: 0;
    margin: 0 0 4px 0;
    display: flex;
    width: 100%;
    text-align: start;
    font-size: rem(14px);
    line-height: 20px;
  }
  &__flair {
    min-width: 14px;
    width: 14px;
    margin-right: 8px;
    &--checked {
      color: var(--positive-light);
    }
    &--unchecked {
      color: var(--perfect-gray);
    }
    &-info {
      margin-top: 2px;
      &--unchecked {
        color: var(--perfect-gray);
      }
    }
  }
  &__btn {
    color: var(--white);
    border-color: var(--orange);
    &--unselected {
      --btn-secondary-text-color: var(--lighter-gray);
      --btn-border-color: var(--lighter-gray);
    }
    @include mobile {
      position: absolute;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
    }
  }
}
</style>
